import { useState } from 'react'
import type { Note, NoteType, Site } from '@shared/types'
import { patch } from '../lib/api'
import { formatDateTime, relativeTime } from '../lib/format'
import { useToast } from '../state/toast'
import { Chip, Sheet } from './ui'

interface NoteCardProps {
  note: Note
  noteTypes: NoteType[]
  timezone: string
  showAuthor?: boolean
  /** Admin only. Omitted for the worker's own feed. */
  onAcknowledge?: (note: Note) => void
  selected?: boolean
  onSelect?: (note: Note, selected: boolean) => void
  /**
   * Both present = the site is editable: workers on their own feed, the office
   * anywhere. An update posted from the wrong site mis-files the whole thing,
   * so the fix sits right on the card.
   */
  sites?: Site[]
  onSiteChanged?: (note: Note) => void
}

export function NoteCard({
  note, noteTypes, timezone, showAuthor, onAcknowledge, selected, onSelect, sites, onSiteChanged,
}: NoteCardProps) {
  const toast = useToast()
  const [lightbox, setLightbox] = useState<string | null>(null)
  const [siteSheet, setSiteSheet] = useState(false)
  const [busy, setBusy] = useState(false)
  const type = noteTypes.find((t) => t.key === note.type)
  const needsAck = Boolean(type?.needsAck) && !note.acknowledgedAt
  const siteEditable = Boolean(sites?.length && onSiteChanged)

  const moveTo = async (site: Site | null) => {
    if (site?.id === note.siteId) {
      setSiteSheet(false)
      return
    }
    setBusy(true)
    try {
      const res = await patch<{ note: Note }>(`/api/notes/${note.id}`, { siteId: site?.id ?? null })
      setSiteSheet(false)
      toast.success(site ? `Moved to ${site.name}` : 'Site removed')
      onSiteChanged?.(res.note)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not change the site')
    } finally {
      setBusy(false)
    }
  }

  return (
    <article
      className={`card overflow-hidden ${needsAck ? 'border-l-4 border-l-alarm' : ''}`}
    >
      <div className="flex items-start gap-3 p-3">
        {onSelect && (
          <input
            type="checkbox"
            className="mt-1 size-5 shrink-0 accent-[var(--wm-brand-deep)]"
            checked={selected ?? false}
            onChange={(e) => onSelect(note, e.target.checked)}
            aria-label={`Select update from ${note.userName ?? 'worker'}`}
          />
        )}

        <div className="min-w-0 flex-1">
          <div className="mb-1.5 flex flex-wrap items-center gap-2">
            <Chip color={type?.color ?? '#6B6560'}>{type?.label ?? note.type}</Chip>
            <time
              dateTime={note.occurredAt}
              className="font-mono text-xs whitespace-nowrap text-graphite-2 tnum"
            >
              {formatDateTime(note.occurredAt, timezone)}
            </time>
            {siteEditable ? (
              <button
                type="button"
                onClick={() => setSiteSheet(true)}
                aria-label={`Change site — currently ${note.siteName ?? 'no site'}`}
                className="min-w-0 truncate text-xs font-semibold text-graphite-2 underline decoration-dotted underline-offset-2"
              >
                {note.siteName ?? 'No site'}
              </button>
            ) : (
              note.siteName && (
                <span className="truncate text-xs font-semibold text-graphite-2">
                  {note.siteName}
                </span>
              )
            )}
            {showAuthor && note.userName && (
              <span className="truncate text-xs font-bold">{note.userName}</span>
            )}
          </div>

          {note.body && <p className="text-sm whitespace-pre-wrap">{note.body}</p>}

          {note.attachments.length > 0 && (
            <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
              {note.attachments.map((attachment) => (
                <button
                  key={attachment.id}
                  type="button"
                  onClick={() => setLightbox(attachment.url)}
                  className="shrink-0"
                >
                  <img
                    src={attachment.thumbUrl ?? attachment.url}
                    alt="Progress photo"
                    loading="lazy"
                    className="size-20 rounded border border-line object-cover"
                  />
                </button>
              ))}
            </div>
          )}

          {note.acknowledgedAt && (
            <p className="mt-2 font-mono text-[10px] tracking-wider text-ok uppercase">
              Acknowledged by {note.acknowledgedByName ?? 'the office'} ·{' '}
              {relativeTime(note.acknowledgedAt)}
              {note.ackComment && (
                <span className="mt-1 block normal-case tracking-normal text-graphite-2">
                  “{note.ackComment}”
                </span>
              )}
            </p>
          )}
        </div>

        {onAcknowledge && !note.acknowledgedAt && (
          <button
            type="button"
            onClick={() => onAcknowledge(note)}
            className="btn btn-secondary shrink-0 px-3 text-xs"
            style={{ minHeight: 38 }}
          >
            Acknowledge
          </button>
        )}
      </div>

      {siteSheet && (
        <Sheet open onClose={() => setSiteSheet(false)} title="Which site was this at?">
          <div className="space-y-2" aria-busy={busy}>
            {sites!.map((site) => (
              <button
                key={site.id}
                type="button"
                disabled={busy}
                onClick={() => void moveTo(site)}
                className="card flex w-full items-center gap-3 p-3 text-left"
              >
                <span
                  className="size-3 shrink-0 rounded-full"
                  style={{ background: site.color }}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 truncate font-bold">{site.name}</span>
                {site.id === note.siteId && (
                  <span className="chip bg-ink text-brand">current</span>
                )}
              </button>
            ))}
            <button
              type="button"
              disabled={busy}
              onClick={() => void moveTo(null)}
              className="card flex w-full items-center gap-3 p-3 text-left"
            >
              <span className="size-3 shrink-0 rounded-full bg-line" aria-hidden />
              <span className="min-w-0 flex-1 truncate font-bold text-graphite-2">No site</span>
              {!note.siteId && <span className="chip bg-ink text-brand">current</span>}
            </button>
          </div>
        </Sheet>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-graphite/90 p-4"
          role="dialog"
          aria-label="Photo"
        >
          <button
            type="button"
            className="absolute inset-0"
            aria-label="Close photo"
            onClick={() => setLightbox(null)}
          />
          <img
            src={lightbox}
            alt="Progress photo, full size"
            className="relative max-h-full max-w-full object-contain"
          />
        </div>
      )}
    </article>
  )
}
